/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { useEffect } from 'react';

import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { InteractiveComponents } from '../../../core/enums';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { CursorManager } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { setOneLineGuideTurboScrollAction } from '../../../store/videoplayerui/onelineguide/onelineguide.actions';
import {
    LONG_CLICK_INTERVAL_TIME,
    TURBOSCROLL_INTERVAL_TIME,
} from '../../../store/videoplayerui/onelineguide/onelineguide.constants';
import {
    onKeyDownOneLineGuide,
    onKeyLeftOneLineGuide,
    onKeyRightOneLineGuide,
    onKeyUpOneLineGuide,
} from '../../../store/videoplayerui/onelineguide/onelineguide.navigation.reducers';
import { getMetadataForChannel } from '../../../store/videoplayerui/onelineguide/onelineguide.reducers';
import { hideWhisper, showWhisper } from '../../../store/whisper/whisper.reducer';
import { EmptyGuideMessage, Mousetrap } from '../../common';
import { useUpdateEffect } from '../../hooks';
import { Whisper } from '../../Whisper';
import { WhisperTypes } from '../../Whisper/WhisperTypes';
import { HeaderOneLineGuide } from './HeaderOneLineGuide';
import { useCursorOptions } from './hooks/useCursorOptions';
import { EPISODIC_ASSET_WIDTH, MOVIE_ASSET_WIDTH } from './onelineguide.constants';
import { OneLineGuideChannelList } from './OneLineGuideChannelList';
import { OneLineGuideContentStrip } from './OneLineGuideContentStrip';
import { OneLineGuideInteractiveLayer } from './OneLineGuideInteractiveLayer';
import { PreviewOneLineGuide } from './PreviewOneLineGuide';
import { TimebarOneLineGuide, TimebarOneLineGuidePlaceholder } from './TimebarOneLineGuide';

import './OneLineGuide.scss';

const OneLineGuide: React.FC = React.memo(() => {
    const {
        cellList,
        currentSelectedCell: selectedCell,
        error,
        isEmpty,
        timeStamp,
        isFutureMode,
        selectedChannel,
        oneLineGuideTurboScroll,
        oneLineGuideTurboScrollDirection,
        oneLineGuideHorizontalDirection,
    } = useTivoSelector((state: RootState) => state.oneLineGuide);

    const dispatch = useTivoDispatch();

    const { activeComponent, hideMessage, hideDescription } = useCursorOptions();

    const currentSelectedCellWidth: number = DimensionScaler.getInstance().getScaledWidth(
        selectedCell?.contentType === 'movie' ? MOVIE_ASSET_WIDTH : EPISODIC_ASSET_WIDTH,
    );

    const className = `one-line-guide-container ${
        isFutureMode ? 'olg-future-mode' : 'olg-past-mode'
    }`;

    const isDisconnectedState = error && cellList.length === 0;
    const isNetworkDisconnected = NetworkManager.isNetworkDisconnected() ?? false;

    const hideHeaderMessage = hideMessage || isDisconnectedState || isNetworkDisconnected;

    const whisperMessage = () => {
        if (isNetworkDisconnected) {
            return NetworkManager.getNetworkDisconnectedMessage();
        }
        if (isDisconnectedState) {
            return Locale.getString(LocaleKeys.GUIDE_WHISPER_MESSAGE);
        }
        return '';
    };

    // When the user disconnects and changes the channel, error becomes available and cellList becomes empty.
    // Upon reconnection, we need to reload the cell list.
    // Used useUpdateEffect instead of useEffect because the logic is needed only when the state changes from disconnected to connected,
    // avoiding any side effects on initial mount.
    useUpdateEffect(() => {
        if (isNetworkDisconnected) {
            return;
        }
        if (cellList.length === 0 && error !== null) {
            dispatch(getMetadataForChannel(selectedChannel, timeStamp));
        }
    }, [isNetworkDisconnected]);

    useEffect(() => {
        let intervalId: null | NodeJS.Timeout = null;

        intervalId = setInterval(() => {
            if (oneLineGuideTurboScroll) {
                if (oneLineGuideTurboScrollDirection === InteractiveComponents.NORTH) {
                    dispatch(onKeyUpOneLineGuide(false));
                } else {
                    dispatch(onKeyDownOneLineGuide(false));
                }
            }
        }, TURBOSCROLL_INTERVAL_TIME);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [oneLineGuideTurboScroll, oneLineGuideTurboScrollDirection]);

    useEffect(() => {
        if (isDisconnectedState || isNetworkDisconnected) {
            dispatch(setOneLineGuideTurboScrollAction(false));
            dispatch(showWhisper());
        } else {
            dispatch(hideWhisper());
        }
    }, [isDisconnectedState, isNetworkDisconnected, dispatch]);

    useEffect(() => {
        let intervalId: null | NodeJS.Timeout = null;

        intervalId = setInterval(() => {
            if (oneLineGuideHorizontalDirection === InteractiveComponents.EAST) {
                dispatch(onKeyRightOneLineGuide());
            }
            if (oneLineGuideHorizontalDirection === InteractiveComponents.WEST) {
                dispatch(onKeyLeftOneLineGuide());
            }
        }, LONG_CLICK_INTERVAL_TIME);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [oneLineGuideHorizontalDirection]);

    const visibleLeftCaret = cellList.length > 0 && selectedCell && cellList[0] !== selectedCell;
    const visibleRightCaret =
        cellList.length > 0 && selectedCell && cellList[cellList.length - 1] !== selectedCell;

    if (isEmpty) {
        return (
            <div className="one-line-guide-container empty">
                <div style={{ marginLeft: '30.05vw' }}>
                    <EmptyGuideMessage />
                </div>
                <Whisper
                    message={error?.getDescription() || whisperMessage()}
                    iconType={WhisperTypes.INFO}
                />
                {CursorManager.isCursorAppeared() && (
                    <Mousetrap className={'empty-one-line-guide-interactive-layer'} value={''} />
                )}
            </div>
        );
    }

    return (
        <div className={className}>
            <OneLineGuideChannelList />
            <div className="olg-channels-content-container">
                <HeaderOneLineGuide pastMode={!isFutureMode} hideMessage={hideHeaderMessage} />
                {selectedCell && !isNetworkDisconnected && !oneLineGuideTurboScroll ? (
                    <TimebarOneLineGuide
                        startTime={selectedCell?.startTime}
                        duration={selectedCell?.duration}
                        cellWidth={currentSelectedCellWidth}
                    />
                ) : (
                    <TimebarOneLineGuidePlaceholder />
                )}
                {oneLineGuideTurboScroll ? (
                    <div className="olg-channels-channel-number">
                        {selectedChannel.channelNumber}
                    </div>
                ) : (
                    <OneLineGuideContentStrip />
                )}
                {!hideDescription && !oneLineGuideTurboScroll && !isNetworkDisconnected && (
                    <PreviewOneLineGuide />
                )}
            </div>
            {CursorManager.isCursorAppeared() && (
                <OneLineGuideInteractiveLayer
                    activeComponent={activeComponent}
                    pastMode={!isFutureMode}
                    isMovie={selectedCell?.contentType === 'movie'}
                    visibleLeftCaret={visibleLeftCaret}
                    visibleRightCaret={visibleRightCaret}
                />
            )}
            <Whisper
                message={error?.getDescription() || whisperMessage()}
                iconType={WhisperTypes.INFO}
            />
        </div>
    );
});

export default OneLineGuide;
